import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Layout } from '@/shell/Layout'
import { CopyBlock } from '@/shared/components/CopyBlock'
import type { OrgToken } from '@/lib/apiTypes'
import { useOrgTokens, useOrgs } from '@/lib/hooks'
import { GeoCell } from '@/features/tokens/components/GeoCell'
import { Badge } from '@/shared/components/Badge'
import { CreateTokenModal } from '@/features/tokens/components/CreateTokenModal'
import { ConfirmModal } from '@/shared/components/ConfirmModal'
import { Modal } from '@/shared/components/Modal'
import { api } from '@/lib/api'
import { formatDate, formatDateTime } from '@/lib/format'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonTable } from '@/shared/components/Skeleton'
import { FeedbackMessage } from '@/shared/components/FeedbackMessage'
import { useToast } from '@/context/ToastContext'
import { Tooltip } from '@/shared/components/Tooltip'

export function AllTokens() {
  const { orgSlug } = useParams()
  const orgs = useOrgs()
  const selectedOrg = orgs.data?.orgs.find((o) => (o.slug ?? o.id) === orgSlug)
  const orgsLoading = orgs.loading
  const [showCreate, setShowCreate] = useState(false)

  const isAdmin = ['owner', 'admin'].includes(selectedOrg?.role ?? '')

  const [refetchTick, setRefetchTick] = useState(0)

  return (
    <Layout title="// Bot Tokens">
      {/* {selectedOrg && (
        <Breadcrumbs>
          <BreadcrumbLink to={`/org/${orgSlug}/settings`}>Org</BreadcrumbLink>
          <BreadcrumbSeparator />
          <span>Bot Tokens</span>
        </Breadcrumbs>
      )} */}
      {showCreate && selectedOrg && (
        <CreateTokenModal
          orgId={selectedOrg.slug}
          orgName={selectedOrg.name}
          onClose={() => { setShowCreate(false); setRefetchTick((k) => k + 1) }}
          onCreated={() => setRefetchTick((k) => k + 1)}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <span />
        {isAdmin && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
            + New token
          </button>
        )}
      </div>

      <div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {selectedOrg ? (
            <OrgTokenList
              key={`${selectedOrg.slug}-${refetchTick}`}
              orgId={selectedOrg.slug}
              isAdmin={isAdmin}
            />
          ) : (
            !orgsLoading && <EmptyState icon="⬡" title="No organisations found." />
          )}
        </div>
      </div>
    </Layout>
  )
}

function OrgTokenList({ orgId, isAdmin }: { orgId: string; isAdmin: boolean }) {
  const tokens = useOrgTokens(orgId)
  const { showToast } = useToast()
  const [busy, setBusy] = useState<string | null>(null)
  const [confirmToken, setConfirmToken] = useState<OrgToken | null>(null)
  const [confirmAction, setConfirmAction] = useState<'rotate' | 'delete' | null>(null)
  const [rotatedSecret, setRotatedSecret] = useState<{ tokenName: string; clientId: string; secret: string } | null>(null)
  const [rotateConfirmed, setRotateConfirmed] = useState(false)
  const [viewingToken, setViewingToken] = useState<OrgToken | null>(null)

  async function handleRotate(t: OrgToken) {
    setBusy(t.id)
    setConfirmToken(null)
    setConfirmAction(null)
    try {
      const res = await api.post<{ client_secret: string }>(`/api/orgs/${orgId}/tokens/${t.id}/rotate`, {})
      setRotateConfirmed(false)
      setRotatedSecret({ tokenName: t.name, clientId: t.oidc_client_id, secret: res.client_secret })
      tokens.refetch()
    } catch (err) {
      showToast((err as Error).message, 'error')
    } finally {
      setBusy(null)
    }
  }

  async function handleDelete(t: OrgToken) {
    setBusy(t.id)
    setConfirmToken(null)
    setConfirmAction(null)
    try {
      await api.delete(`/api/orgs/${orgId}/tokens/${t.id}`)
      showToast(`Token "${t.name}" deleted.`, 'success')
      tokens.refetch()
    } catch (err) {
      showToast((err as Error).message, 'error')
    } finally {
      setBusy(null)
    }
  }

  if (tokens.loading) return (
    <SkeletonTable
      headers={['Name', 'Client ID', 'Scope', 'Created', 'Last used', 'Last location', '']}
      firstColTwoLines={false}
    />
  )
  if (tokens.error) return <FeedbackMessage ok={false} msg={tokens.error} />

  return (
    <>
      {rotatedSecret && (
        <Modal
          track="bot_token_secret_rotated"
          title={`New secret for ${rotatedSecret.tokenName}`}
          kicker="// rotate secret"
          onClose={() => setRotatedSecret(null)}
          maxWidth={540}
          dismissable={false}
        >
          <div style={{ fontSize: '0.82rem', color: 'var(--text2)', marginBottom: 16 }}>
            Save these credentials - they will <strong style={{ color: 'var(--amber)' }}>never be shown again</strong>.
          </div>
          <div style={{ marginBottom: 12 }}>
            <div className="field-label">Client ID</div>
            <CopyBlock value={rotatedSecret.clientId} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <div className="field-label">Client Secret</div>
            <CopyBlock value={rotatedSecret.secret} />
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text3)', marginBottom: 16, lineHeight: 1.5 }}>
            In the <code style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>client_credentials</code> grant, the fixed <code style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>client_id</code> is <code style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>terramantle-bots</code>; the Client ID goes in <code style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>username</code> and the Client Secret in <code style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>password</code>.
          </div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: '0.78rem', color: 'var(--text2)', lineHeight: 1.5, marginBottom: 16 }}>
            <input
              type="checkbox"
              checked={rotateConfirmed}
              onChange={(e) => setRotateConfirmed(e.target.checked)}
              style={{ marginTop: 2, flexShrink: 0 }}
            />
            <span>I've saved the client secret somewhere safe.</span>
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary btn-sm" onClick={() => setRotatedSecret(null)} disabled={!rotateConfirmed}>
              Close
            </button>
          </div>
        </Modal>
      )}

      {viewingToken && (
        <TokenDetailModal
          token={viewingToken}
          isAdmin={isAdmin}
          onClose={() => setViewingToken(null)}
          onRotate={(t) => { setViewingToken(null); setConfirmToken(t); setConfirmAction('rotate') }}
          onDelete={(t) => { setViewingToken(null); setConfirmToken(t); setConfirmAction('delete') }}
        />
      )}

      {confirmToken && confirmAction && (
        <ConfirmModal
          track={confirmAction === 'rotate' ? 'bot_token_rotate_confirm' : 'bot_token_delete_confirm'}
          title={confirmAction === 'rotate' ? 'Rotate secret' : 'Delete token'}
          message={
            confirmAction === 'rotate'
              ? `Rotate secret for "${confirmToken.name}"? The current secret will stop working immediately.`
              : `Delete "${confirmToken.name}"? This cannot be undone.`
          }
          confirmLabel={confirmAction === 'rotate' ? 'Rotate' : 'Delete'}
          danger={confirmAction === 'delete'}
          busy={busy === confirmToken.id}
          onConfirm={() => confirmAction === 'rotate' ? handleRotate(confirmToken) : handleDelete(confirmToken)}
          onCancel={() => { setConfirmToken(null); setConfirmAction(null) }}
        />
      )}

      {(tokens.data?.tokens.length ?? 0) > 0 ? (
        <div className="table-wrap fu">
          <table>
            <thead>
              <tr>
                <th>
                  <Tooltip text="The token's name, as set when it was created." placement="top">
                    <span tabIndex={0} className="cursor-help">Name</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="OIDC client id this token authenticates as." placement="top">
                    <span tabIndex={0} className="cursor-help">Client ID</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="What the token can reach: global, the whole org, or a set of state workspaces." placement="top">
                    <span tabIndex={0} className="cursor-help">Scope</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="When the token was created." placement="top">
                    <span tabIndex={0} className="cursor-help">Created</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="When the token last authenticated." placement="top">
                    <span tabIndex={0} className="cursor-help">Last used</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="IP and geo-location of the token's last use." placement="top">
                    <span tabIndex={0} className="cursor-help">Last location</span>
                  </Tooltip>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tokens.data!.tokens.map((t) => (
                <tr key={t.id}>
                  <td>
                    <code style={{ fontFamily: 'var(--mono)', fontSize: '0.72rem', color: 'var(--text2)' }}>
                      {t.name}
                    </code>
                  </td>
                  <td>
                    <code style={{ fontFamily: 'var(--mono)', fontSize: '0.72rem', color: 'var(--text2)' }}>
                      {t.oidc_client_id}
                    </code>
                  </td>
                  <td><ScopeBadge token={t} /></td>
                  <td style={{ color: 'var(--text2)', fontSize: '0.82rem' }}>
                    {formatDate(t.created_at)}
                  </td>
                  <td style={{ color: 'var(--text2)', fontSize: '0.82rem' }}>
                    {t.last_used_at ? formatDate(t.last_used_at) : '-'}
                  </td>
                  <td><GeoCell ip={t.last_source_ip} country={t.last_country} city={t.last_city} /></td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setViewingToken(t)}>
                        View
                      </button>
                      {isAdmin && (
                        <>
                          <button
                            className="btn btn-ghost btn-sm"
                            disabled={busy === t.id}
                            onClick={() => { setConfirmToken(t); setConfirmAction('rotate') }}
                          >
                            Rotate
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            disabled={busy === t.id}
                            onClick={() => { setConfirmToken(t); setConfirmAction('delete') }}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState icon="⬡" title="No bot tokens yet." />
      )}
    </>
  )
}

function modulesPerm(t: OrgToken): string | null {
  if (t.scope === 'global') return 'write'
  if (t.scope === 'org') return t.include_modules ?? null
  return null
}

function statePerm(t: OrgToken): string | null {
  if (t.scope === 'global') return 'write'
  if (t.scope === 'org') return t.include_state ?? null
  if (t.scope === 'state_workspaces') {
    const perms = [...new Set(t.state_grants.map((g) => g.permission))]
    if (perms.length === 0) return null
    return perms.length === 1 ? perms[0] : 'mixed'
  }
  return null
}

function ScopeBadge({ token }: { token: OrgToken }) {
  if (token.scope === 'global') return <Badge variant="accent">global</Badge>
  if (token.scope === 'org') return <Badge variant="neutral">org</Badge>
  if (token.scope === 'state_workspaces') {
    const count = token.state_grants.length
    return <Badge variant="neutral">{count} workspace{count !== 1 ? 's' : ''}</Badge>
  }
  return <Badge variant="neutral">org</Badge>
}

function PermBadge({ perm }: { perm: string | null }) {
  if (!perm || perm === 'none') return <span style={{ color: 'var(--text3)', fontSize: '0.75rem' }}>-</span>
  const color = perm === 'write' ? 'var(--green)' : perm === 'mixed' ? 'var(--amber)' : 'var(--text2)'
  return <span style={{ fontSize: '0.75rem', color, fontFamily: 'var(--mono)' }}>{perm}</span>
}

function TokenDetailModal({ token: t, isAdmin, onClose, onRotate, onDelete }: {
  token: OrgToken
  isAdmin: boolean
  onClose: () => void
  onRotate: (t: OrgToken) => void
  onDelete: (t: OrgToken) => void
}) {
  return (
    <Modal track="bot_token_detail" title={t.name} kicker="// bot token" onClose={onClose} maxWidth={560}>
      {t.description && <div style={{ fontSize: '0.75rem', color: 'var(--text3)', marginBottom: 16, marginTop: -4 }}>{t.description}</div>}
      <div style={{ display: 'grid', gap: 20 }}>
        {/* Credentials */}
        <div>
          <div className="eyebrow">Credentials</div>
          <div className="field-label">Client ID</div>
          <div className="code-block" style={{ padding: '8px 12px', fontSize: '0.8rem', wordBreak: 'break-all', marginTop: 4 }}>{t.oidc_client_id}</div>
        </div>

        {/* Scope */}
        <div>
          <div className="eyebrow">Access scope</div>
          <div style={{ marginBottom: 10 }}><ScopeBadge token={t} /></div>

          {(t.scope === 'org' || t.scope === 'global') && (
            <div className="stack-4">
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ color: 'var(--text2)' }}>Modules</span>
                <PermBadge perm={modulesPerm(t)} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', padding: '4px 0' }}>
                <span style={{ color: 'var(--text2)' }}>State</span>
                <PermBadge perm={statePerm(t)} />
              </div>
            </div>
          )}

            {t.state_grants.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div className="field-label" style={{ marginBottom: 6 }}>State workspace grants</div>
                <div style={{ display: 'grid', gap: 2 }}>
                  {t.state_grants.map((g) => (
                    <div key={g.workspace_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', background: 'var(--bg3)', borderRadius: 4 }}>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: '0.78rem', color: 'var(--text2)' }}>{g.workspace_name}</span>
                      <PermBadge perm={g.permission} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Usage */}
          <div>
            <div className="eyebrow">Usage</div>
            <div style={{ display: 'grid', gap: 6 }}>
              {[
                { label: 'Created', value: formatDateTime(t.created_at) },
                { label: 'Created by', value: t.created_by_email ?? t.created_by },
                { label: 'Last used', value: t.last_used_at ? formatDateTime(t.last_used_at) : 'Never' },
                { label: 'Last rotated', value: t.last_rotated_at ? formatDateTime(t.last_rotated_at) : 'Never' },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
                  <span className="dim">{label}</span>
                  <span style={{ color: 'var(--text2)', fontFamily: 'var(--mono)', fontSize: '0.78rem' }}>{value}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', padding: '4px 0' }}>
                <span className="dim">Last location</span>
                <GeoCell ip={t.last_source_ip} country={t.last_country} city={t.last_city} />
              </div>
            </div>
          </div>

        {/* Admin actions */}
        {isAdmin && (
          <div style={{ display: 'flex', gap: 8, paddingTop: 4, borderTop: '1px solid var(--border)' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => onRotate(t)}>Rotate secret</button>
            <button className="btn btn-danger btn-sm" onClick={() => onDelete(t)}>Delete token</button>
          </div>
        )}
      </div>
    </Modal>
  )
}

